import { Flex, Skeleton, Text } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { QuickLink } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/QuickLinksCard/quickLinks.types.ts';
import {
	QUICK_LINKS_TILE_HEIGHT,
	QuickLinkTile,
	QuickLinkTileLink,
	QuickLinksGrid,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/QuickLinksCard/QuickLinksCard.styles.tsx';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	links: QuickLink[];
	isLoading: boolean;
	isError: boolean;
}

export const QuickLinksSection = ({ links, isLoading, isError }: Props) => {
	const tMipManage = useTranslationMipManage();

	if (isLoading) {
		return (
			<div>
				<Text fw={600} c="dark.8" mb="0.75rem">
					{tMipManage('investors.details.quick_links.title')}
				</Text>

				<QuickLinksGrid>
					{links.map((link) => (
						<Skeleton key={link.key} height={QUICK_LINKS_TILE_HEIGHT} radius="0.5rem" />
					))}
				</QuickLinksGrid>
			</div>
		);
	}

	return (
		<div>
			<Text fw={600} c="dark.8" mb="0.75rem">
				{tMipManage('investors.details.quick_links.title')}
			</Text>

			<QuickLinksGrid data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_QUICK_LINKS}>
				{links.map((link) => {
					const testId = makeComplexDataTestId(
						TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_QUICK_LINK,
						link.key
					);

					const content = (
						<>
							<Flex direction="column" className="quick-links-left">
								<span className="icon-badge">
									<Icon variant={link.icon} size="vlg" />
								</span>

								<Text fz={14} c="dark.8" fw={600} truncate>
									{link.label}
								</Text>
							</Flex>

							<Flex direction="column" className="quick-links-right">
								{isError && (
									<Text fz={12} c="dark.4">
										{LONG_DASH}
									</Text>
								)}

								{!isError && !!link.total && (
									<span className="total-count">
										<Text fz={12} c="dark.4">
											{link.total}
										</Text>
									</span>
								)}
							</Flex>
						</>
					);

					return isError || !link.total ? (
						<QuickLinkTile key={link.key} data-testid={testId}>
							{content}
						</QuickLinkTile>
					) : (
						<QuickLinkTileLink key={link.key} to={link.path} data-testid={testId}>
							{content}
						</QuickLinkTileLink>
					);
				})}
			</QuickLinksGrid>
		</div>
	);
};
